import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subscription, tap } from 'rxjs';
import { LoadAlerts } from 'src/app/alert/state/alert.actions';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { IDueListCounts } from 'src/app/due-list/models/due-list-counts.model';
import { MainMenuListModel } from '../../models/menu-list.model';
import { ClearFilterDetails } from '../../state/main-table-filter.actions';
import { SaveNewMenuState } from '../../state/menu-list.actions';
import { MenuListState, MenuStateModel } from '../../state/menu-list.state';
import {
  ADMIN_MENU_LIST_ROUTES,
  getUserMenuListRoutes,
  getUserMenuListRoutesForAllDeps,
} from './menu-list-routes';
import { MainTableFilterModulesEnum } from '../../enums/main-table-filter-modules.enum';

@Component({
  selector: 'app-menu-list',
  templateUrl: './menu-list.component.html',
  styleUrls: ['./menu-list.component.scss'],
  standalone: false,
})
export class MenuListComponent implements OnInit, OnDestroy {
  getMenuState$: Observable<MenuStateModel | undefined> = this.store.select(
    MenuListState.getMenuState
  );
  currentDepartmentId$ = this.store.select(TenantUserAccessState.currentDepartmentId);
  currentTenantId$: Observable<string> = this.store.select(TenantUserAccessState.currentTenantId);

  mainMenuItems: MainMenuListModel[];
  currentTenantId: string;
  currentMenuState: MenuStateModel;
  isInAdmin: boolean;
  buttonText = 'exitAdmin';
  expiredWarnings = 0;
  soonExpiringWarnings = 0;

  private subscriptions = new Subscription();
  private isProductOwner: boolean;
  private currentDepartmentId: string;

  constructor(
    private store: Store,
    private router: Router,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {}

  ngOnInit(): void {
    this.initSubscriptions();
    this.listenToRouteChanges();
    this.getDueListCounts();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  exitAdmin(): void {
    this.store.dispatch(new ClearFilterDetails());
  }

  trackBy = (index: number) => index;

  private initSubscriptions(): void {
    const alertsFilterAll = new MainTableFilterModel(MainTableFilterModulesEnum.Alerts);
    alertsFilterAll.pageIndex = 0;
    // eslint-disable-next-line no-magic-numbers
    alertsFilterAll.pageSize = 10000;
    this.subscriptions.add(
      this.currentTenantId$.subscribe((tenantId) => {
        if (tenantId && this.currentTenantId !== tenantId) {
          this.currentTenantId = tenantId;
          this.store.dispatch(new LoadAlerts(alertsFilterAll));
        }
      })
    );

    this.subscriptions.add(
      this.currentDepartmentId$.subscribe((depId: string) => {
        if (depId && depId !== this.currentDepartmentId) {
          this.currentDepartmentId = depId;
          this.mainMenuItems = this.loadAppropriateMenuItems(
            this.currentMenuState?.isInAdmin ?? false
          );
        }
      })
    );

    this.subscriptions.add(
      this.getMenuState$.subscribe((getMenuState) => {
        if (getMenuState) {
          this.currentMenuState = getMenuState;
          this.mainMenuItems = this.loadAppropriateMenuItems(getMenuState.isInAdmin);
        }
      })
    );
  }

  private listenToRouteChanges(): void {
    if (this.router.url === '/') {
      this.store.dispatch(new SaveNewMenuState('/'));
    }
    this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationEnd &&
        this.isInAdmin &&
        this.currentDepartmentId === ALL_DEPS_TOKEN
      ) {
        this.store.dispatch(new SaveNewMenuState('/'));
        this.mainMenuItems = this.getUserRoutes();
      } else if (event instanceof NavigationEnd && this.currentDepartmentId !== ALL_DEPS_TOKEN) {
        if (!this.alreadyHasAppropriateItems(event.url))
          this.mainMenuItems = this.loadAppropriateMenuItems(this.isUrlInAdmin(event.url));
        this.store.dispatch(new SaveNewMenuState(event.url));
      }
    });
  }

  private alreadyHasAppropriateItems(url: string): boolean {
    if (!this.mainMenuItems) return false;
    if (this.isUrlInAdmin(url) && this.isInAdmin) return true;
    return !this.isUrlInAdmin(url) && !this.isInAdmin;
  }

  private loadAppropriateMenuItems(isInAdmin: boolean): MainMenuListModel[] {
    this.isInAdmin = isInAdmin;

    if (!this.currentTenantId) {
      return [];
    }

    return this.isInAdmin ? this.getAdminRoutes() : this.getUserRoutes();
  }

  private isUrlInAdmin(url: string): boolean {
    const urlFragments = url.split('/');
    return urlFragments.some((urlFragment: string) => urlFragment === 'admin');
  }

  private getUserRoutes(): MainMenuListModel[] {
    if (this.currentDepartmentId === ALL_DEPS_TOKEN) {
      return this.tenantUserPrivileges.recursiveFilterMenuItemsPerPrivileges(
        getUserMenuListRoutesForAllDeps()
      );
    }
    const currentDepartment = this.store.selectSnapshot(TenantUserAccessState.currentDepartment);
    const crewManagementType = currentDepartment?.crewManagementType;
    return this.tenantUserPrivileges.recursiveFilterMenuItemsPerPrivileges(
      getUserMenuListRoutes({ crewManagementType })
    );
  }

  private getAdminRoutes(): MainMenuListModel[] {
    let adminRoutes =
      this.tenantUserPrivileges.recursiveFilterMenuItemsPerPrivileges(ADMIN_MENU_LIST_ROUTES);
    this.isProductOwner = this.store.selectSnapshot(TenantUserAccessState.isProductOwner) ?? false;
    if (!this.isProductOwner) {
      adminRoutes = adminRoutes.filter((route: MainMenuListModel) => route.name !== 'productOwner');
    }
    return adminRoutes;
  }

  getDueListCounts(): void {
    this.subscriptions.add(
      this.store
        .select(DueListState.getDueListCounts)
        .pipe(
          tap((counts: IDueListCounts | undefined) => {
            if (counts) {
              this.expiredWarnings = counts.expiredWarnings + counts.expiredCompetences;
              this.soonExpiringWarnings = counts.expiringWarnings + counts.expiringCompetences;
            }
          })
        )
        .subscribe()
    );
  }
}
